// ─── La scheda di una sua lezione, su un telefono ────────────────────────────
//
// 🗣️ 18/09/2026: *«una sezione dove possono prenotare le lezioni, inserire i
// giocatori»* · *«si deve vedere bene sul cellulare»*.
//
// 🎾 **COSA C'È E COSA NO, ed è la stessa riga del server.** Si vede chi c'è in
// campo, quanto deve ciascuno, e si mette o si toglie un allievo. Non c'è la
// cassa, non c'è la scheda del socio, non c'è il prezzo da scrivere: l'importo
// lo propone il listino (🗣️ *«vede gli importi, non incassa»*).
//
// ⛔ E la difesa non è questa schermata: è il server. Qui non compare un bottone
//    «incassa» perché non esiste nessuna rotta di cassa dietro questa porta —
//    non perché l'abbiamo nascosto (⇒ `features/auth/sessione.tsx`: il frontend
//    nasconde per cortesia, non per sicurezza).
//
// 🆕⭐⭐ **LA SCHEDA SI TOCCA TUTTA — 20/09/2026, tappa ②.** Quando · Quanto
// dura · Dove non sono più tre cose scritte in una riga di testo: sono **tre
// righe che si premono**, e ciascuna apre il modo di cambiarla.
// ⚖️ Prima lo spostamento era un bottone («Sposta») e la durata un blocchetto
// in fondo, mentre **il campo non si poteva cambiare affatto** se non spostando
// tutto. ⇒ Le tre coordinate di una lezione adesso si leggono e si cambiano
// **nello stesso posto**, che è dove uno le cerca.
// 📌 *Un valore che si legge in un punto e si cambia in un altro è un valore
//    che si cerca due volte.*

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  DURATA_DEL_TIPO,
  formatCents,
  minutiDaOra,
  NOTA_MASSIMA,
  oraDaMinuti,
  type MiaLezione,
  type SpazioDelMaestro,
  type VoltaAnnullata,
} from '@pmo/shared';
import { chiama, ErroreApi } from '../../lib/api.js';
import { Attesa, Guasto } from '../../components/pezzi.js';
import { giornoBreve, giornoPerEsteso } from '../../lib/time.js';
import { ETICHETTA_TIPO, rinfrescaLaGiornata } from './pezzi.js';
import { CercaSocio, type SocioTrovato } from './CercaSocio.js';
import { ScegliDurata, scriviDurata } from './Durata.js';
import { FoglioBasso } from './FoglioBasso.js';
import { RigaFatta, RigaFerma, RigaVerso } from './Righe.js';
import {
  buttaBozza,
  cambiaLaCollocazione,
  conCambio,
  leggiBozza,
  pulisci,
  quante,
  quantiAvvisati,
  scriviBozza,
  useBozza,
  vista,
  type Modifiche,
  type Vista,
} from './bozza.js';

/**
 * Quale foglio è aperto. `null` = nessuno, ed è lo stato normale.
 * 🆕 `lascio` (voce 17): si sta uscendo con delle modifiche non confermate.
 */
type Aperto = 'durata' | 'campo' | 'nota' | 'lascio' | null;

export function Lezione(): React.JSX.Element {
  const { id = '' } = useParams();
  const vaiA = useNavigate();
  const coda = useQueryClient();
  const chiave = ['maestro', 'lezione', id];
  const [chiedeConferma, setChiedeConferma] = useState(false);
  /* 🪝 Letto QUI, prima dei due `return` anticipati: un hook chiamato dopo di
     loro esiste solo quando la lezione è arrivata, e React vede cambiare
     l'ordine degli hook fra il primo disegno e il secondo (misurato il 23/09:
     «React has detected a change in the order of Hooks called by Lezione»).
     Serve a `appena`, più sotto. */
  const arrivo = useLocation().state as { appena?: 'prenotata' | 'spostata' } | null;
  const [foglio, setFoglio] = useState<Aperto>(null);
  /**
   * 📝 La nota **mentre la si scrive**, che non è la nota che c'è: `null`
   * vuol dire «il foglio non è aperto». Tenerla nella cassa delle query
   * vorrebbe dire che ogni lettera battuta è una risposta del server riscritta.
   */
  const [testoNota, setTestoNota] = useState<string | null>(null);
  /**
   * ✏️⭐⭐ **LE MODIFICHE NON ANCORA CONFERMATE — voce 17 (23/09/2026).**
   * Durata, campo, ora e nota non si scrivono più al tocco: vanno qui, e
   * partono tutte insieme da «Conferma le modifiche» (⇒ `bozza.ts`).
   */
  const bozzaGrezza = useBozza(id);
  /** ✅ Com'è andata l'ultima conferma: la notizia in cima alla scheda. */
  const [salvate, setSalvate] = useState<{ avvisati: number } | null>(null);
  /**
   * 🧹 Si è già cambiato qualcosa in questa visita: da quel momento la notizia
   * con cui si è arrivati («Prenotata — …») è vecchia. 📏 Misurato nel browser:
   * senza, dopo «Modifiche salvate» e un cambio nuovo ricompariva «Prenotata»,
   * cioè una notizia di prima al posto di nessuna.
   */
  const [toccata, setToccata] = useState(false);
  /** ⏱️ Perché una durata non si può scegliere: si dice dentro il foglio. */
  const [durataNo, setDurataNo] = useState<string | null>(null);

  const lezione = useQuery({
    queryKey: chiave,
    queryFn: () => chiama<MiaLezione>('GET', `/maestro/lezioni/${id}`),
  });

  /**
   * ⭐ Le scritture rendono la lezione **rifatta**, e la si mette in cassa al
   * posto di quella vecchia invece di richiederla: da un telefono è un giro di
   * rete in meno, e soprattutto non c'è nessun istante in cui si vede l'elenco
   * di prima.
   */
  const mettoInCassa = (l: MiaLezione): void => {
    coda.setQueryData(chiave, l);
    // La giornata porta gli stessi nomi: se resta com'era, tornando indietro si
    // legge un conto vecchio.
    rinfrescaLaGiornata(coda);
  };

  /**
   * 🆕⭐⭐ **O UN SOCIO, O UN NOME — e i due campi viaggiano SEMPRE tutti e due**
   * (tappa ⑤, 20/09/2026).
   *
   * ⚖️ Mandarne uno solo funzionerebbe lo stesso — il server mette l'altro a
   * `null` da sé — ma qui si scrive la forma vera della regola: lo schema
   * pretende che **uno e uno solo** dei due sia pieno, e una chiamata che
   * nomina entrambi lo fa vedere a chi legge invece di lasciarlo dedurre.
   * 🚨 E c'è un motivo misurato: sul codice di ieri mandare `guestName` a uno
   *    schema che non lo conosceva rispondeva **200 buttandolo via in
   *    silenzio**. ⇒ *Uno schema stretto non rifiuta ciò che non conosce: lo
   *    scarta* — e chi l'ha mandato legge un «fatto» che non è successo.
   */
  const aggiungi = useMutation({
    meta: { fatto: 'allievo aggiunto alla lezione' },
    mutationFn: (chi: { memberId: string | null; guestName: string | null }) =>
      chiama<MiaLezione>('POST', `/maestro/lezioni/${id}/allievi`, chi),
    onSuccess: mettoInCassa,
  });
  const togli = useMutation({
    meta: { fatto: 'allievo tolto dalla lezione' },
    mutationFn: (allievoId: string) =>
      chiama<MiaLezione>('DELETE', `/maestro/lezioni/${id}/allievi/${allievoId}`),
    onSuccess: mettoInCassa,
  });
  /**
   * 🚨⭐⭐ **«NON C'ERA» — e NON è «Togli» con un'altra parola.**
   *
   * 🗣️ *«Il corso si paga al mese»* (19/09) ⇒ si paga il **posto**, non la
   * presenza: l'assente resta in elenco col suo importo, e il totale del corso
   * non cala. Togliere invece lo toglie davvero — riga e importo — ed è il
   * gesto giusto per chi in quel corso non c'entra.
   * 📌 *Due gesti che sullo schermo si somigliano possono essere opposti nel
   *    libro.*
   */
  const assenza = useMutation({
    meta: {
      fatto: (_d, v) => ((v as { assente: boolean }).assente ? 'segnato assente' : 'segnato presente'),
    },
    mutationFn: (v: { allievoId: string; assente: boolean }) =>
      chiama<MiaLezione>('PATCH', `/maestro/lezioni/${id}/allievi/${v.allievoId}`, {
        assente: v.assente,
      }),
    onSuccess: mettoInCassa,
  });

  /**
   * ✅⭐⭐ **CONFERMARE — un gesto solo per tutto quello che è cambiato** (voce 17).
   *
   * ⚖️ **Le due scritture di sempre, in fila, e non una rotta nuova.** Giorno,
   * ora, durata e campo viaggiano già **insieme** in `PATCH /lezioni/:id` —
   * che controlla il perimetro, il passato e il campo preso, e manda **un**
   * avviso (`spostata`, o `durata` se cambia solo quella). La nota ha la sua
   * (`PATCH /lezioni/:id/nota`), che **non avvisa nessuno** e vale anche su una
   * lezione passata: resta separata apposta (⇒ tappa ⑦a).
   * 🚨 **E se la seconda va male, la prima resta fatta — e si dice.** Ogni
   *    scrittura riuscita toglie le sue chiavi dalla bozza nel momento in cui
   *    torna: così quello che resta nella barra è esattamente quello che non è
   *    ancora salvato, e ripremere non rifà la parte già andata.
   * ⛔ Le coordinate si leggono da `lezione.data` **nell'istante del tocco**,
   *    non da una copia presa prima: fra l'apertura della scheda e il dito può
   *    esserci passato un cambio della segreteria.
   */
  const confermo = useMutation({
    meta: {
      fatto: (d) => {
        const n = (d as { avvisati: number }).avvisati;
        return n === 0
          ? 'lezione confermata'
          : `lezione confermata: ${n === 1 ? '1 allievo avvisato' : `${n} allievi avvisati`}`;
      },
    },
    mutationFn: async (): Promise<{ avvisati: number }> => {
      const l = lezione.data!;
      const m = pulisci(l, leggiBozza(id));
      const w = vista(l, m);
      const avvisati = quantiAvvisati(l, m);
      if (cambiaLaCollocazione(m)) {
        mettoInCassa(
          await chiama<MiaLezione>('PATCH', `/maestro/lezioni/${id}`, {
            courtId: w.courtId,
            date: w.date,
            time: w.time,
            durationMinutes: w.durationMinutes,
          }),
        );
        scriviBozza(id, m.nota === undefined ? {} : { nota: m.nota });
      }
      if (m.nota !== undefined) {
        mettoInCassa(
          await chiama<MiaLezione>('PATCH', `/maestro/lezioni/${id}/nota`, { nota: m.nota ?? '' }),
        );
        buttaBozza(id);
      }
      return { avvisati };
    },
    onSuccess: (e) => {
      setSalvate(e);
      /* ⭐ Si torna in cima: la notizia «salvate» sta lì, e chi ha appena
         premuto in fondo alla pagina non la vedrebbe. */
      window.scrollTo(0, 0);
    },
  });

  /**
   * ⏱️⭐ **La durata si controlla QUANDO la si sceglie, non alla conferma.**
   *
   * ⚖️ Il campo e l'ora li offre già solo liberi chi li mostra (il foglio del
   * campo, la griglia delle ore); la durata no — sono tre bottoni fissi. Senza
   * questa domanda, «2 ore» si metterebbe in bozza su un campo occupato alle
   * 20:00, e il rifiuto arriverebbe solo premendo «Conferma», insieme a tutto
   * il resto.
   * ⭐ La domanda la fa il server, ed è quella di sempre: `GET /spazi` con la
   *    durata nuova, cercando la lezione **com'è in bozza** (giorno, ora,
   *    campo). Nessuna seconda idea di cosa sia libero.
   * ⚠️ Rimettere la durata di prima non si chiede: è tornare com'era.
   */
  const provoDurata = useMutation({
    meta: { fatto: () => null },
    mutationFn: async (d: number): Promise<{ d: number; libera: boolean }> => {
      const l = lezione.data!;
      const w = vista(l, pulisci(l, leggiBozza(id)));
      const dopo = conCambio(l, leggiBozza(id), { durationMinutes: d });
      if (!cambiaLaCollocazione(dopo)) return { d, libera: true };
      const r = await coda.fetchQuery({
        queryKey: ['maestro', 'spazi', w.date, d, l.id],
        queryFn: () =>
          chiama<{ items: Array<Omit<SpazioDelMaestro, 'date'>> }>(
            'GET',
            `/maestro/spazi?date=${w.date}&durationMinutes=${d}&escludi=${l.id}`,
          ),
      });
      return { d, libera: r.items.some((x) => x.time === w.time && x.courtId === w.courtId) };
    },
    onSuccess: ({ d, libera }) => {
      const l = lezione.data!;
      if (!libera) {
        const w = vista(l, pulisci(l, leggiBozza(id)));
        setDurataNo(
          `Il ${w.courtName} non è libero per te fino alle ${oraDaMinuti(minutiDaOra(w.time) + d)}: così la lezione non può durare ${scriviDurata(d)}.`,
        );
        return;
      }
      cambio({ durationMinutes: d });
      setFoglio(null);
    },
  });

  /** ✏️ Un cambio entra nella bozza, e la notizia di prima non vale più. */
  const cambio = (c: Modifiche): void => {
    const l = lezione.data!;
    scriviBozza(id, conCambio(l, leggiBozza(id), c));
    setSalvate(null);
    setToccata(true);
    confermo.reset();
  };

  const annulla = useMutation({
    meta: {
      fatto: (_d, r) =>
        (r as { recupera: boolean }).recupera ? 'lezione annullata, da recuperare' : 'lezione annullata',
      // 🆕 Fase 3 (R6): la lezione della sera a meno di 48 ore — prima si dice cosa costa.
      domande: ['TRATTENUTA_DA_CONFERMARE'],
    },
    mutationFn: (v: { recupera: boolean; accettoLaTrattenuta?: boolean }) =>
      chiama<VoltaAnnullata>('POST', `/maestro/lezioni/${id}/annulla`, v),
    onSuccess: (e) => {
      /* ⭐ Gli spazi liberi si rinfrescano **dentro** `rinfrescaLaGiornata` dal
         20/09, e non più qui: l'ora appena liberata deve ricomparire fra quelle
         prendibili, esattamente come quella appena presa deve sparirne (⇒
         `pezzi.ts`, dove sta il difetto misurato). */
      rinfrescaLaGiornata(coda);
      /* ⭐ Si va via e non si resta qui: annullata, questa volta non è più una
         cosa su cui fare gesti. Restarci davanti con i bottoni spenti sarebbe
         una schermata che non serve più a niente — e per di più, se su quella
         riga non era passato niente, non esiste nemmeno più da rileggere.
         🎓⭐⭐ **E DOVE si va non è sempre lo stesso posto**: annullare una volta
            di corso è un fatto **del corso** — è lì che si vede se la promessa
            del recupero è stata mantenuta, perché l'ultima volta si è spostata
            di una settimana. Una lezione qualunque invece torna alla giornata.
         📌 *Il risultato di un gesto si mostra dove il gesto si vede, non dove
            si stava prima.* */
      /* ⚠️ Si rilegge da `lezione.data` e non dalla variabile della schermata:
         questa funzione nasce a ogni disegno, e quella variabile non esiste
         ancora quando il disegno si ferma prima (caricamento, guasto). */
      const q = lezione.data;
      /* 🧹 Quello che era in bozza parlava di una lezione che non c'è più. */
      buttaBozza(id);
      if (q?.type === 'corso' && q.serieId !== null)
        vaiA(`/maestro/corsi/${q.serieId}`, { state: { annullo: e } });
      else vaiA('/maestro');
    },
  });

  if (lezione.isLoading) return <Attesa cosa="la lezione" />;
  if (lezione.error) return <Guasto errore={lezione.error} />;
  const l = lezione.data!;
  const annullata = l.status === 'cancelled';

  /**
   * ✅⭐⭐ **CHE COSA È APPENA SUCCESSO** — voce 12 (21/09/2026).
   *
   * 🗣️ Da una sua segnalazione: arrivando qui dopo aver preso un'ora, l'unica
   * cosa che si leggeva era **«Annulla la lezione»** — il verbo per disfare,
   * su una scheda che non diceva di aver fatto niente.
   *
   * ⚖️ **Lo porta chi ha fatto il gesto, e non si indovina da qui.** Una scheda
   * non può sapere se ci si è arrivati adesso da una prenotazione o da un
   * collegamento di tre giorni fa: dedurlo da un orario appena scritto sarebbe
   * una risposta giusta qualche volta.
   * ⛔ E sparisce da sé alla rilettura, perché lo stato della navigazione non
   *    sopravvive a un ricaricamento: è una **notizia**, non uno stato della
   *    lezione, e una notizia che resta smette di esserlo.
   */
  const appena = arrivo?.appena ?? null;
  /* 🔄⭐⭐ **NON È PIÙ TUA — tappa ⑦c, 20/09/2026.** La lezione c'è ancora, ma
     la tiene un altro maestro: la scheda resta raggiungibile **apposta** — è
     l'unica cosa che evita a chi l'aveva in agenda di andare al campo — e non
     ha nessun gesto, perché non c'è niente su cui possa agire.
     ⛔ E il server la pensa uguale: le scritture passano da una lettura
        stretta, che di lezioni non sue non ne trova (⇒ `miaPrenotazione`).
        Qui si **spiega**, là si **rifiuta**. */
  const altrui = l.passataA !== null;
  /* ⛔ Ferma vuol dire *non si tocca più la collocazione*, e sono TRE casi
     diversi con lo stesso effetto su queste tre righe: quella annullata non è
     successa, quella passata è successa e non si disfa, quella di un altro non
     è roba su cui decidere. */
  const ferma = annullata || l.passata || altrui;
  /* ✏️ **Quali modifiche contano adesso** (voce 17). Su una annullata o di un
     altro nessuna: non c'è niente da confermare. Su una passata solo la nota —
     è l'unica cosa che lì si scrive ancora. E ognuna si rilegge contro la
     lezione di **adesso**: se nel frattempo è diventata com'era in bozza, quella
     non è più una modifica. */
  const m: Modifiche =
    annullata || altrui
      ? {}
      : l.passata
        ? pulisci(l, bozzaGrezza.nota === undefined ? {} : { nota: bozzaGrezza.nota })
        : pulisci(l, bozzaGrezza);
  const w = vista(l, m);
  const daConfermare = quante(m);
  const fine = oraDaMinuti(minutiDaOra(w.time) + w.durationMinutes);
  /* 📏 `giornoBreve` e non il giorno per esteso: in una riga larga quanto un
     telefono «lunedì 21 settembre · 09:00» va **a capo**, e una riga che si
     spezza in due sta male accanto alle altre due che non si spezzano — visto
     nella fotografia del 20/09. Quella funzione esiste apposta («per i bottoni
     stretti», ⇒ `lib/time.ts`). */
  const quando = `${giornoBreve(w.date)} · ${w.time}`;
  const quandoEra =
    m.date !== undefined || m.time !== undefined ? `${giornoBreve(l.date)} · ${l.time}` : undefined;
  /* 🎓 Una volta di corso, e non una lezione: `serieId` c'è **solo** se questa
     riga appartiene davvero a una serie — il tipo da solo non basterebbe, un
     corso senza periodicità non ha una coda a cui aggiungere niente. */
  const diCorso = l.type === 'corso' && l.serieId !== null;
  /* 🏋️ Voce 21: la stessa scheda della lezione, con quattro parole diverse e
     la durata ferma — un'ora e mezza la dice il tipo (⇒ `maestro/tipo.ts`,
     dove la regola vive davvero). */
  const allenamento = l.type === 'allenamento';
  /* ⏱️ Voce 22: lezione e allenamento durano quanto dice il tipo, e la riga
     sta ferma. Una lezione nata prima della regola con un'altra durata la
     tiene, e allora non si scrive «fissa» — sarebbe una parola falsa. */
  const durataDelTipo = l.type === 'lezione' || l.type === 'allenamento' ? DURATA_DEL_TIPO[l.type] : null;

  return (
    <>
      <p>
        {/* 🚪 Con delle modifiche in sospeso non si esce in silenzio: si
            perderebbero senza che nessuno l'abbia deciso (voce 17). */}
        {daConfermare > 0 ? (
          <button type="button" className="indietro" onClick={() => setFoglio('lascio')}>
            ‹ La mia agenda
          </button>
        ) : (
          <Link className="indietro" to="/maestro">
            ‹ La mia agenda
          </Link>
        )}
      </p>

      <section className="riquadro">
        {/* ⭐ Prima di tutto il resto, e prima dell'annullo: la prima cosa che
            si legge arrivando dev'essere che è andata. */}
        {salvate !== null && (
          <p className="appena-fatta">
            <span className="segno" aria-hidden="true">
              ✓
            </span>
            <span>
              Modifiche salvate
              {salvate.avvisati > 0 && (
                <>
                  {' — '}
                  <strong>
                    {salvate.avvisati === 1 ? '1 allievo avvisato' : `${salvate.avvisati} allievi avvisati`}
                  </strong>
                </>
              )}
            </span>
          </p>
        )}
        {salvate === null && !toccata && appena !== null && !annullata && (
          <p className="appena-fatta">
            <span className="segno" aria-hidden="true">
              ✓
            </span>
            <span>
              {appena === 'prenotata'
                ? allenamento
                  ? 'Prenotato'
                  : 'Prenotata'
                : allenamento
                  ? 'Spostato'
                  : 'Spostata'}{' '}
              —{' '}
              <strong>
                {giornoPerEsteso(l.date)}, {l.time}, {l.courtName}
              </strong>
            </span>
          </p>
        )}
        <div className="capo-scheda">
          <h2>
            {w.time}–{fine}
          </h2>
          <span className="pastiglia">{ETICHETTA_TIPO[l.type] ?? l.type}</span>
        </div>
        {l.title !== null && <p className="tenue">{l.title}</p>}

        {altrui ? (
          /* ➕ Resta, e dice CHI la tiene: «sparita» e «data a Giorgio» sono
             due notizie diverse, e solo la seconda evita un viaggio al campo.
             ⭐ E dice anche la cosa che si fa con quell'ora: **è tornata
                libera**. Senza, un maestro che l'ha persa continuerebbe a
                considerarla occupata. */
          <p className="capo-righe capo-morto">
            Questa lezione <strong>non è più tua</strong>: adesso la tiene <strong>{l.passataA}</strong>. Te
            l’ha spostata la segreteria, e quell’ora per te è di nuovo libera.
          </p>
        ) : annullata ? (
          /* ⚠️ Una lezione annullata resta raggiungibile dal suo indirizzo — chi
             ci torna indietro deve leggere **che cos'è**, non trovare una scheda
             normale con dei bottoni che rispondono di no. */
          <p className="capo-righe">
            Questa lezione è <strong>annullata</strong>: resta qui perché tu sappia che c’era, e il campo è
            libero.
          </p>
        ) : l.passata ? (
          /* 🕰️⭐⭐ **UNA LEZIONE PASSATA È UN REGISTRO, non una scheda spenta.**
             🗣️ La regola è del gestionale, non di questa schermata: *«il passato
             che non si tocca è QUANDO e DOVE si è giocato, non chi c'era»*
             (⇒ `calendario/regole.ts`). ⇒ Le tre righe diventano **ferme** — non
             spariscono: dicono ancora dov'era e quanto è durata — e resta vivo
             l'unico gesto che ha senso il giorno dopo: correggere chi c'era.
             📌 *Una schermata di sola lettura su una lezione fatta non è
                prudenza: è perdere l'unico momento in cui si sa chi c'era.* */
          <p className="capo-righe">
            {allenamento ? (
              <>
                Questo allenamento è <strong>già passato</strong>
              </>
            ) : (
              <>
                Questa lezione è <strong>già passata</strong>
              </>
            )}
            : si corregge chi c’era, il resto no.
          </p>
        ) : null}

        {/* ⭐⭐ LE TRE RIGHE, che sono la stessa cosa in due stati: ferme dicono
            **com'era**, vive si premono e aprono il modo di cambiarla. Toglierle
            del tutto su una lezione passata vorrebbe dire non poter più sapere
            dove si era giocato. */}
        <div className="righe-maestro">
          {ferma ? (
            <>
              <RigaFerma che="Quando" valore={quando} />
              <RigaFerma che="Quanto dura" valore={scriviDurata(l.durationMinutes)} />
              <RigaFerma che="Dove" valore={l.courtName} />
            </>
          ) : (
            <>
              {/* 📅 «Quando» porta via, e le altre due no: cambiare giorno e ora
                  vuol dire guardare la giornata intera, che è una schermata. */}
              <RigaVerso
                che="Quando"
                valore={quando}
                era={quandoEra}
                verso={`/maestro/lezioni/${l.id}/sposta`}
                stato={{ daScheda: true }}
              />
              {durataDelTipo !== null ? (
                <RigaFerma
                  che="Quanto dura"
                  valore={`${scriviDurata(w.durationMinutes)}${w.durationMinutes === durataDelTipo ? ', fissa' : ''}`}
                />
              ) : (
                <RigaFatta
                  che="Quanto dura"
                  valore={scriviDurata(w.durationMinutes)}
                  era={m.durationMinutes !== undefined ? scriviDurata(l.durationMinutes) : undefined}
                  apri={() => {
                    setDurataNo(null);
                    provoDurata.reset();
                    setFoglio('durata');
                  }}
                />
              )}
              <RigaFatta
                che="Dove"
                valore={w.courtName}
                era={m.campo !== undefined ? l.courtName : undefined}
                apri={() => setFoglio('campo')}
              />
            </>
          )}
        </div>

        {/* 🔄 **E il verso opposto: te l'hanno passata.** Senza, in agenda
            comparirebbe una lezione che il maestro giurerebbe di non aver mai
            prenotato — e la prima cosa che farebbe è telefonare in segreteria
            per chiedere di chi è.
            ⚠️ Solo sulle vive: su una annullata la notizia è un'altra, e su una
               che non è più sua è **il contrario**. */}
        {l.arrivataDa !== null && !annullata && (
          <p className="capo-righe">
            Te l’ha passata la segreteria: prima la teneva <strong>{l.arrivataDa}</strong>. Da qui la gestisci
            tu.
          </p>
        )}

        {/* 🎓⭐⭐ **DI CHE CORSO È QUESTA VOLTA.** Senza questa riga, «annulla e
            recuperala alla fine» sarebbe una promessa su una cosa che non si
            vede: il maestro sta allungando **un corso**, e da qui non sapeva
            nemmeno quale.
            ⚖️ E l'avviso d'ambito sta accanto, non dentro i fogli: le tre righe
            qui sopra valgono **solo per questa volta** — chi ha appena cambiato
            la durata di tutto un corso dalla sua scheda potrebbe credere il
            contrario. */}
        {diCorso && (
          <>
            <RigaVerso
              che="Fa parte di"
              valore={l.title ?? 'Corso'}
              lungo
              verso={`/maestro/corsi/${l.serieId!}`}
            />
            {!ferma && (
              <p className="regola">
                Le tre righe qui sopra valgono <strong>solo per questa volta</strong>: alle altre il corso
                resta com’è.
              </p>
            )}
          </>
        )}

        {/* 📝⭐⭐ **LA TUA NOTA — tappa ⑦a, 20/09/2026.**
            ⚖️ **Non è `notes` della segreteria**, che questa schermata non
            legge e non leggerà: sono due note di **due persone diverse sullo
            stesso fatto**, e su un campo solo chi scrive per secondo cancella
            l'altro (⇒ `migrations/1758380000000_la_mia_nota.cjs`).
            ⛔ **Non su una annullata**: di una cosa che non si è giocata non
               c'è niente da appuntare, e il server rifiuta comunque
               (⇒ `maestro/nota.ts`). ⭐ **Su una PASSATA sì**, ed è il caso che
               conta: la scheda di una lezione fatta è un registro, e il momento
               in cui si sa com'è andata è subito dopo. */}
        {/* ⛔ E nemmeno su una che non è più tua: di una lezione che tiene un
            altro non c'è niente da appuntare — e il server non la scriverebbe
            comunque (⇒ la `where` di `scrivoLaMiaNota`). */}
        {!annullata && !altrui && (
          <RigaFatta
            che="La tua nota"
            /* ✂️ Una riga sola, coi puntini: il testo intero si legge nel
               foglio che questa riga apre. È la scelta del mockup approvato,
               e la misura che la regge sta in `base.css`. */
            tagliato
            valore={w.nota ?? '— aggiungila'}
            era={m.nota !== undefined ? (l.nota ?? 'nessuna') : undefined}
            apri={() => {
              setTestoNota(w.nota ?? '');
              setFoglio('nota');
            }}
          />
        )}
      </section>

      <section className="riquadro">
        {/* 👁️ **«Chi c'era» su una lezione fatta** — trovato guardando la
            fotografia: su un registro il presente fa rileggere due volte, e la
            domanda a cui quella schermata risponde è al passato. */}
        {/* 👁️🔄 Su una che non è più tua il titolo lo dice: «Chi c'è» da solo
            si legge come «i tuoi allievi», e non lo sono più. */}
        <h2>{altrui ? 'Chi ci sarà' : l.passata ? 'Chi c’era' : 'Chi c’è'}</h2>
        {l.players.length === 0 ? (
          <p className="tenue">
            {l.passata
              ? 'Non è segnato nessuno. Se c’era qualcuno, lo si aggiunge qui sotto.'
              : 'Ancora nessuno. Qui sotto si cerca un socio per nome.'}
          </p>
        ) : (
          <ul className="elenco-allievi">
            {l.players.map((g) => (
              /* ⭐ Il NOME si smorza e si sbarra, l'IMPORTO no: quella persona
                 in campo non c'era, ma il posto era suo e lo paga. Uno stile
                 che smentisse la regola scritta due righe sotto vincerebbe
                 lui — si guarda prima di leggere. */
              <li key={g.id} className={g.assente ? 'assente' : undefined}>
                <span>
                  {/* ⚠️ Il nome sta in uno span SUO, e non è una scatola in più:
                      in CSS una barratura **attraversa i discendenti** e non si
                      annulla dal figlio. 👁️ Trovato guardando la fotografia —
                      «· non c'era» veniva sbarrato insieme al nome, cioè lo
                      stile diceva che anche quella parola non valeva. */}
                  <span className="nome">{g.displayName}</span>
                  {/* 🆕⭐⭐ **«· ospite», con le parole che la segreteria USA
                      GIÀ** (`Scheda.tsx`): senza, un ospite di nome «Luca
                      Marini» è indistinguibile dal socio che si chiama così —
                      ed è proprio il caso in cui il maestro deve sapere quale
                      dei due ha davanti.
                      ⚖️ Sta **fuori** da `.nome` per la stessa ragione di «non
                         c'era»: una barratura attraversa i discendenti, e
                         sbarrare anche questa parola direbbe che l'ospite non
                         è più un ospite.
                      ⚠️ Lo spazio è un `\u00a0`: quello normale, scritto qui,
                         JSX lo mangia — e si leggerebbe «Luca Marini· ospite». */}
                  {g.ospite && <span className="tenue piccolo etichetta-ospite">{'\u00a0'}· ospite</span>}
                  {/* 👁️ E il «·» se n'è andato: va a capo per conto suo, e un
                      punto appeso a inizio riga sembra un difetto. */}
                  {g.assente && <span className="da-quando">non c’era</span>}
                </span>
                {/* 💶🔄 **E su una che non è più tua, niente cifre** — 👁️
                    trovato guardando la fotografia, non rileggendo: la riga
                    dell'agenda toglie l'importo perché *«quei soldi non sono
                    più suoi»*, e questa scheda lo mostrava lo stesso. Due
                    schermate che raccontano lo stesso fatto in modi opposti
                    non sono due stili: una delle due dice il falso.
                    ⚖️ L'ELENCO invece resta: sapere chi ci sarebbe stato è
                       metà della notizia. Ciò che cambia di mano col maestro
                       sono i soldi, non le persone. */}
                {!altrui && <span className="soldi">{formatCents(g.amountDueCents)}</span>}
                {/* 🎯 UN bottone per riga, e sempre quello giusto: su una volta
                    di corso un ISCRITTO si segna assente, mentre chi è stato
                    aggiunto a mano a quella sola volta si toglie — segnarlo
                    assente lo lascerebbe a pagare un posto che non è suo.
                    📏 E due bottoni non ci starebbero: `.elenco-allievi li` è
                    tre colonne, e a 320 px una quarta esce dal bordo. */}
                {/* ⛔🔄 Su una lezione che non è più tua NON c'è nessun
                    bottone: di chi c'è in campo decide chi la tiene adesso, e
                    il server rifiuterebbe comunque (⇒ `miaPrenotazione`).
                    ⚖️ L'elenco invece resta: sapere **chi ci sarebbe stato** è
                       metà della notizia — è la lezione che si stava per
                       andare a fare. */}
                {altrui ? null : diCorso && g.iscritto ? (
                  <button
                    type="button"
                    className="gesto-riga"
                    disabled={assenza.isPending}
                    onClick={() => assenza.mutate({ allievoId: g.id, assente: !g.assente })}
                  >
                    {g.assente ? 'Rimetti' : 'Assente'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="gesto-riga"
                    disabled={togli.isPending}
                    onClick={() => togli.mutate(g.id)}
                  >
                    Togli
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {l.players.length > 0 && !altrui && (
          /* 💶 Il totale **a carico**, che non è un incasso: la parola conta,
             perché da questa porta nessuno incassa niente.
             🔄 E non su una che non è più tua: ⇒ la riga qui sopra. */
          <p className="tenue piccolo">A carico in tutto: {formatCents(l.totalDueCents)}</p>
        )}
        {/* 🏋️ Da dove viene la cifra, detto: se entra un terzo cambia per tutti,
            e senza questa riga sembrerebbe un errore. */}
        {allenamento && l.players.length > 0 && !altrui && (
          <p className="regola">
            Il prezzo è quello del <strong>Listino prezzi lezioni</strong>, a testa per{' '}
            {['uno', 'due', 'tre', 'quattro'][Math.min(4, l.players.length) - 1]}: se entra o esce qualcuno,
            si ricalcola per tutti.
          </p>
        )}
        {/* 🗣️ La regola si SCRIVE, o il maestro la deve indovinare dal fatto
            che un numero non è cambiato. E si scrive solo dove vale: su una
            lezione singola non c'è nessun posto tenuto da pagare. */}
        {diCorso && l.players.some((g) => g.assente) && (
          <p className="regola">
            Chi <strong>manca</strong> paga lo stesso: il posto resta suo. E al socio{' '}
            <strong>non arriva niente</strong>.
          </p>
        )}
        <Guasto errore={togli.error} />
        <Guasto errore={assenza.error} />
      </section>

      {/* ⭐ E qui NON si guarda `passata`: su una lezione fatta si aggiunge chi
          c'era e non era segnato. È la stessa riga del server, che su
          `POST /lezioni/:id/allievi` non chiama `controllaPassato`. */}
      {!annullata && !altrui && (
        <CercaSocio
          titolo="Aggiungi un allievo"
          /* 🚨⭐⭐ **SOLO I SOCI, e la tappa ⑤ l'ha reso necessario.** Questo
             elenco serve a spegnere il nome di chi è **già in campo**; se ci
             entrassero anche gli ospiti, un ospite chiamato «Luca Marini»
             spegnerebbe il **socio** Luca Marini con scritto «già in campo» —
             una frase falsa su una persona che in campo non c'è.
             ⚖️ E all'incontrario non si spegne niente: due ospiti omonimi il
                circolo li ammette apposta (l'indice unico dei soci è parziale,
                ⇒ `migrations/1757990000000_prenotazioni.cjs`). Un ospite non è
                un'identità, quindi non c'è nessun «già» da dire. */
          giaDentro={l.players.filter((g) => !g.ospite).map((g) => g.displayName)}
          comeCiSta="già in campo"
          scegli={(s: SocioTrovato) => aggiungi.mutate({ memberId: s.id, guestName: null })}
          /* 🆕⭐⭐ **IL CONFINE SI LEGGE DAL TIPO, ed è lo stesso del server**
             (⇒ `maestro/ospiti.ts`, dove la regola vive davvero: qui si
             **spiega**, là si **rifiuta**). ⛔ E non è `diCorso`: quello vuole
             anche una serie, mentre una volta di corso rimasta senza serie è
             un corso lo stesso — e lì un ospite non ci va comunque.
             ⚖️ **Perché un corso no**: un corso si paga al mese e si paga il
                **posto** ⇒ chi è dentro è un *iscritto*, cioè una riga su tutte
                le volte che restano, con un importo per ciascuna e una scheda a
                cui addebitarle. Un ospite non è un'identità: scriverlo lì
                sarebbe dodici importi a carico di nessuno. */
          ospiti={
            l.type === 'lezione' || allenamento
              ? {
                  fai: (nome: string) => aggiungi.mutateAsync({ memberId: null, guestName: nome }),
                }
              : {
                  niente:
                    l.type === 'corso' ? (
                      <>
                        In un corso ci vanno gli <strong>iscritti</strong>: un ospite si scrive su una lezione
                        o un allenamento.
                      </>
                    ) : (
                      <>
                        Un ospite si scrive solo su una <strong>lezione</strong> o un{' '}
                        <strong>allenamento</strong>.
                      </>
                    ),
                }
          }
          inCorso={aggiungi.isPending}
          errore={aggiungi.error}
        />
      )}

      {/* 🔻⭐ **L'ANNULLO STA IN FONDO, largo e rosso — voce 17.** 🗣️ *«i due
          bottoni […] sono posizionati e graficamente messi in maniera orrenda
          e sbagliata»*. Stava sotto le tre righe, un collegamento piatto
          accanto a tutto il resto: il gesto più grave della scheda era il più
          facile da toccare per sbaglio e il più difficile da trovare apposta.
          ⇒ In fondo, dopo tutto quello che si fa di solito, staccato, e col
          colore che dice cosa fa. */}
      {!ferma && (
        <div className="annullo-in-fondo">
          {chiedeConferma ? (
            /* ⚖️ La conferma è **in linea**, non un dialogo: su un telefono un
               dialogo copre la schermata, e chi lo apre per sbaglio non vede
               più cosa stava annullando. Qui la lezione resta sotto gli occhi
               mentre si decide. */
            <div className="conferma-rossa">
              <p>
                {diCorso
                  ? 'Annullo solo questa volta? Il corso resta.'
                  : allenamento
                    ? 'Annullo davvero questo allenamento?'
                    : 'Annullo davvero questa lezione?'}
              </p>
              {/* 🔁⭐⭐ **IL RECUPERO SI DECIDE QUI, nell'attimo in cui si
                  annulla, e non dopo**: è adesso che il maestro sa se quella
                  volta il circolo la ridarà. 🗣️ Sua scelta del 19/09: «il
                  corso finisce una settimana dopo» — niente conto di crediti
                  per persona.
                  📌 *Una domanda fatta al momento giusto sostituisce un
                     registro: chiederlo dopo vorrebbe dire tenere il conto di
                     chi deve cosa.* */}
              {diCorso && (
                <button
                  type="button"
                  className="pericolo"
                  disabled={annulla.isPending}
                  onClick={() => annulla.mutate({ recupera: true })}
                >
                  Sì, e la recupero alla fine
                </button>
              )}
              <button
                type="button"
                className="pericolo"
                disabled={annulla.isPending}
                onClick={() => annulla.mutate({ recupera: false })}
              >
                {diCorso ? 'Sì, una volta in meno' : 'Sì, annulla'}
              </button>
              <button type="button" className="piatto" onClick={() => setChiedeConferma(false)}>
                No, lascia stare
              </button>
            </div>
          ) : (
            <button type="button" className="piatto-rosso" onClick={() => setChiedeConferma(true)}>
              {diCorso
                ? 'Annulla questa volta'
                : allenamento
                  ? 'Annulla l’allenamento'
                  : 'Annulla la lezione'}
            </button>
          )}
        </div>
      )}
      {annulla.error instanceof ErroreApi && annulla.error.codice === 'TRATTENUTA_DA_CONFERMARE' ? (
        /* 🆕 Fase 3 (R6): cosa costa, detto con garbo, e la strada senza costo. */
        <div className="conferma-rossa">
          <p>
            <strong>⚠️ {annulla.error.message}</strong>
          </p>
          <button type="button" className="piatto" onClick={() => annulla.reset()}>
            Tienila
          </button>
          <button
            type="button"
            className="pericolo"
            disabled={annulla.isPending}
            onClick={() => annulla.mutate({ recupera: false, accettoLaTrattenuta: true })}
          >
            Disdici lo stesso
          </button>
        </div>
      ) : (
        <Guasto errore={annulla.error} />
      )}

      {foglio === 'durata' && (
        <FoglioBasso titolo="Quanto dura" chiudi={() => setFoglio(null)}>
          <ScegliDurata
            durata={w.durationMinutes}
            scegli={(d) => {
              setDurataNo(null);
              provoDurata.mutate(d);
            }}
            disabilitato={provoDurata.isPending}
          />
          {durataNo !== null && (
            <p className="guasto" role="alert">
              {durataNo}
            </p>
          )}
          <p className="regola">
            L’ora di inizio non si muove: cambia solo <strong>a che ora finisce</strong>.
          </p>
          <Guasto errore={provoDurata.error} />
        </FoglioBasso>
      )}

      {foglio === 'nota' && (
        <FoglioBasso titolo="La tua nota" chiudi={() => setFoglio(null)}>
          <div className="campo">
            <textarea
              aria-label="La tua nota su questa lezione"
              rows={4}
              maxLength={NOTA_MASSIMA}
              autoFocus
              value={testoNota ?? ''}
              onChange={(e) => setTestoNota(e.target.value)}
            />
          </div>
          {/* ✏️ «Tieni», non «Salva»: dal 23/09 (voce 17) la nota va nella
              bozza con le altre modifiche, e si salva confermando.
              🔄 **E un bottone pieno, largo quanto il foglio** — 🗣️ guardando
              l'anteprima: *«non c'è un bottone, c'è solo una scritta […] devi
              farlo a bottone, così si capisce»*. Era una riga chiara di
              `.elenco-scelte`, lo stile delle scelte fra più voci: per un gesto
              solo sembrava un'etichetta. */}
          <p className="azioni-foglio azioni-larghe">
            <button
              type="button"
              className="azione-principale"
              disabled={(testoNota ?? '').trim() === (w.nota ?? '')}
              onClick={() => {
                cambio({ nota: testoNota ?? '' });
                setFoglio(null);
                setTestoNota(null);
              }}
            >
              {(testoNota ?? '').trim() === '' ? 'Togli la nota' : 'Tieni questa nota'}
            </button>
          </p>
          {/* 🔇 Va detto, o si crede il contrario — è la stessa riga della
              rinomina di un corso: fra i sette gesti che avvisano non ce n'è
              uno per «ha scritto un appunto» (⇒ `avvisi.ts`).
              🔒 E la seconda frase è **misurata**, non promessa: quella colonna
                 non sta nella forma condivisa di una prenotazione, quindi in
                 nessuna scheda della segreteria c'è un posto che la mostri
                 (⇒ `nota.test.ts`, la prova su `PRENOTAZIONE_SQL`). */}
          <p className="regola">
            È un appunto <strong>tuo</strong>: agli allievi non arriva niente, e nella scheda della segreteria
            non compare.
          </p>
        </FoglioBasso>
      )}

      {foglio === 'campo' && (
        <FoglioDelCampo
          l={l}
          w={w}
          chiudi={() => setFoglio(null)}
          scegli={(campo) => {
            cambio({ campo });
            setFoglio(null);
          }}
        />
      )}

      {foglio === 'lascio' && (
        <FoglioBasso titolo="Esci senza confermare?" chiudi={() => setFoglio(null)}>
          <p>
            Hai {daConfermare === 1 ? '1 modifica' : `${daConfermare} modifiche`} non confermate: uscendo si
            perdono, e la lezione resta com’era.
          </p>
          <p className="azioni-foglio azioni-larghe">
            <button type="button" className="azione-principale" onClick={() => setFoglio(null)}>
              Resta e conferma
            </button>
            <button
              type="button"
              className="piatto"
              onClick={() => {
                buttaBozza(id);
                vaiA('/maestro');
              }}
            >
              Esci senza salvarle
            </button>
          </p>
        </FoglioBasso>
      )}

      {/* ✅⭐⭐ **LA BARRA DELLE MODIFICHE — voce 17.** Compare appena qualcosa
          è cambiato, resta ferma in basso mentre si scorre, e dice le due cose
          che servono per decidere: **quante** modifiche, e **chi** lo saprà.
          ⚠️ Lo spazio in fondo alla pagina (`posto-barra`) c'è apposta: senza,
             la barra coprirebbe l'annullo — 📏 misurato sul mockup a 320 e
             360 px. */}
      {daConfermare > 0 && (
        <>
          <div className="posto-barra" aria-hidden="true" />
          <div className="barra-conferma" role="region" aria-label="Modifiche da confermare">
            <Guasto errore={confermo.error} />
            <p>
              {daConfermare === 1 ? '1 modifica' : `${daConfermare} modifiche`} da confermare.{' '}
              <AChiArriva l={l} m={m} />
            </p>
            <button
              type="button"
              className="azione-principale"
              disabled={confermo.isPending}
              onClick={() => confermo.mutate()}
            >
              {confermo.isPending ? 'Un attimo…' : 'Conferma le modifiche'}
            </button>
            <button
              type="button"
              className="piatto"
              disabled={confermo.isPending}
              onClick={() => {
                buttaBozza(id);
                confermo.reset();
              }}
            >
              Lascia com’era
            </button>
          </div>
        </>
      )}
    </>
  );
}

/**
 * 🔔 **Chi lo saprà, detto PRIMA di premere.** È la ragione per cui la conferma
 * esiste: un cambio di ora arriva sul telefono di qualcuno, e chi lo fa deve
 * saperlo mentre decide, non dopo.
 * ⚖️ Il conto è quello del server (⇒ `quantiAvvisati`): gli ospiti non ricevono
 *    niente, e la nota non avvisa nessuno.
 */
function AChiArriva({ l, m }: { l: MiaLezione; m: Modifiche }): React.JSX.Element {
  if (!cambiaLaCollocazione(m))
    return (
      <>
        La nota è tua: <strong>non arriva niente a nessuno</strong>.
      </>
    );
  const n = quantiAvvisati(l, m);
  if (n === 0)
    return l.players.length === 0 ? (
      <>Non c’è ancora nessun allievo da avvisare.</>
    ) : (
      <>
        Gli ospiti non ricevono avvisi: <strong>non arriva niente a nessuno</strong>.
      </>
    );
  return (
    <>
      Confermando,{' '}
      <strong>{n === 1 ? 'l’allievo riceve un avviso' : `i ${n} allievi ricevono un avviso`}</strong> con
      com’è adesso.
    </>
  );
}

/** Una riga che dice e basta: niente freccia, niente tocco. */

/**
 * 📍⭐⭐ **I CAMPI APERTI A QUELL'ORA, non «quel giorno»** — ed è la differenza
 * che rende questo foglio una cosa utile invece di un elenco da controllare.
 *
 * ⚖️ Un perimetro che dà il Campo 3 solo la mattina non dà il Campo 3 a una
 * lezione delle nove di sera: offrirlo vorrebbe dire far toccare a un maestro
 * una riga che il server rifiuta (`FUORI_PERIMETRO`).
 * 📌 *Un perimetro che si controlla a metà non è un perimetro: è un
 *    suggerimento che ogni tanto ha ragione.*
 *
 * ⭐ **La domanda la fa il server, e la fa quella che la fa già**: `GET /spazi`
 * con la durata e il giorno di questa lezione, tenendo solo gli spazi che
 * cominciano **alla sua stessa ora**. Nessuna rotta nuova, e soprattutto
 * nessuna seconda idea di cosa sia «aperto».
 * ⚠️ `escludi` è questa lezione: senza, l'unico campo che non comparirebbe
 *    sarebbe **quello su cui sta adesso**.
 */
function FoglioDelCampo({
  l,
  w,
  chiudi,
  scegli,
}: {
  l: MiaLezione;
  /**
   * 🆕 La lezione **com'è in bozza** (voce 17): se l'ora o la durata sono già
   * cambiate e non ancora confermate, i campi liberi si cercano lì — è lì che
   * la lezione andrà.
   */
  w: Vista;
  chiudi: () => void;
  scegli: (campo: { courtId: string; courtName: string }) => void;
}): React.JSX.Element {
  const spazi = useQuery({
    queryKey: ['maestro', 'spazi', w.date, w.durationMinutes, l.id],
    queryFn: () =>
      chiama<{ items: Array<Omit<SpazioDelMaestro, 'date'>> }>(
        'GET',
        `/maestro/spazi?date=${w.date}&durationMinutes=${w.durationMinutes}&escludi=${l.id}`,
      ),
  });

  const aperti = (spazi.data?.items ?? []).filter((s) => s.time === w.time);
  /* ⚠️ Il campo dove la lezione **è** resta nell'elenco anche quando a
     quell'ora non è fra i suoi: la lezione c'è davvero (l'avrà messa la
     segreteria), e un elenco che non contiene il posto in cui si sta sembra un
     guasto. Ma si dice, qui sotto. */
  const fuoriPerimetro = !aperti.some((s) => s.courtId === w.courtId);
  const tutti = fuoriPerimetro ? [{ courtId: w.courtId, courtName: w.courtName }, ...aperti] : aperti;

  return (
    <FoglioBasso titolo="Dove" chiudi={chiudi}>
      {spazi.isLoading ? (
        <Attesa cosa="i campi liberi" />
      ) : spazi.error ? (
        <Guasto errore={spazi.error} />
      ) : (
        <>
          <ul className="elenco-scelte">
            {tutti.length === 0 ? (
              <li className="tenue">A quest’ora il circolo non ti ha aperto nessun campo.</li>
            ) : (
              tutti.map((s) => (
                <li key={s.courtId}>
                  <button
                    type="button"
                    className={s.courtId === w.courtId ? 'scelto' : ''}
                    aria-pressed={s.courtId === w.courtId}
                    disabled={s.courtId === w.courtId}
                    onClick={() => scegli({ courtId: s.courtId, courtName: s.courtName })}
                  >
                    {s.courtName}
                    {/* ⚠️ Lo spazio è un `\u00a0`: quello normale, scritto qui,
                        JSX lo mangia — e a schermo si legge «Campo 3✓». */}
                    {s.courtId === w.courtId && <span aria-hidden="true">{'\u00a0✓'}</span>}
                  </button>
                </li>
              ))
            )}
          </ul>
          <p className="regola">
            {fuoriPerimetro ? (
              <>
                Il <strong>{w.courtName}</strong> a quest’ora non è fra i tuoi: la lezione c’è lo stesso, ma
                da qui non potresti prenotarlo.
              </>
            ) : (
              <>
                Solo i campi che il circolo ti ha aperto <strong>a quest’ora</strong>, e che sono liberi.
              </>
            )}
          </p>
        </>
      )}
    </FoglioBasso>
  );
}
